import { config } from '@littlebird/sdk';
import { type MutableRefObject } from 'react';

import { useAppShortcut } from '@/features/shortcuts';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { isTouchPrimaryViewport } from '@/utils/touch-primary';

/** Enter stays free for the composer newline, so Mod-Enter is the send key */
export const modEnterSends = (): boolean =>
  config.chat.require_mod_enter_to_send || isTouchPrimaryViewport();

export function useRegisterSendChat(
  inputRef: MutableRefObject<HTMLElement | undefined>,
  {
    isDisabled,
    canSendNow,
    hasQueuedMessage,
    onSendChat,
    onSendNow,
  }: {
    isDisabled: boolean;
    canSendNow: boolean;
    hasQueuedMessage: boolean;
    onSendChat(): void;
    onSendNow(): void;
  }
) {
  const hotkey = modEnterSends()
    ? 'mod+enter, mod+alt+enter'
    : 'enter, mod+enter';

  return useAppShortcut(
    'chat.send',
    (event) => {
      if (!isCapable(AppCapability.HotKeys)) return;

      if (inputRef.current !== document.activeElement) return;

      const isModifierEnter = event.metaKey || event.ctrlKey;
      const shouldInterrupt =
        canSendNow &&
        isModifierEnter &&
        (modEnterSends() ? event.altKey : !event.altKey);
      if (shouldInterrupt) {
        onSendNow();
        return;
      }

      if (!isDisabled) onSendChat();
    },
    {
      combo: hotkey,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [canSendNow, hasQueuedMessage, isDisabled, onSendChat, onSendNow, hotkey]
  );
}
